import type {
  BalanceGridDataRow,
  BalanceGridRow,
  BalanceTableRow,
} from '../types/balanceGrid'
import { balanceLabel, balanceText } from './balanceI18n'
import { isSlotListedInFinder as isListedInFinder } from './balanceFinder'
import {
  isCandidateRow,
  isFilledPlayerSlot,
  isInvoiceableLine,
  isOccupiedLine,
  isOfficialLine,
  isOpenPlayerSlot,
  isPlayerLine,
  isRemindableSlot,
  isViewerRestricted,
  takesNewMoney,
} from './balanceRowKind'

/** Narrowing guard — renderers, the keyboard handler and totals all need it. */
export const isDataRow = (
  row: BalanceGridRow | undefined | null
): row is BalanceGridDataRow => row?.kind === 'row'

export interface KebabItem {
  key: string
  label: string
  danger?: boolean
  /**
   * Sep 23 (D11) — shown but not actionable, with `disabledReason` as its tooltip:
   * the payer of an official line sees Cancel Invoice and is told only a league
   * admin can use it, rather than wondering where it went.
   */
  disabled?: boolean
  disabledReason?: string
  onClick: () => void
}

export interface KebabHandlers {
  /** C-5 — opens the per-row payments Drawer (now the kebab's first action). */
  onViewPayments: (slotId: string) => void
  onDeleteSlot: (slotId: string) => void
  onLockSlot: (slotId: string) => void
  onUnlockSlot: (slotId: string) => void
  onReleaseSlot: (slotId: string) => void
  onRecordOffline: (slotId: string) => void
  onRemindSlot: (slotId: string) => void
  /**
   * SN-4709 follow-up — open the full roster Edit Player editor. OPTIONAL: present
   * only when the occupant's roster row is loadable and the caller may edit it;
   * when undefined the kebab omits the Edit Player action entirely.
   */
  onEditPlayer?: (slotId: string) => void
  /**
   * SN-4709 × SN-8081 (D46) — open the position picker to LIST this open slot in
   * the Player Finder.
   */
  onListInFinder?: (slotId: string) => void
  /** D46 — untick this slot's Player Finder listing. */
  onRemoveFromFinder?: (slotId: string) => void
  /**
   * Change the position a LISTED open slot recruits for. A position edit, never
   * an opt-in: it does not turn the team's recruiting back on.
   */
  onEditFinderPosition?: (slotId: string) => void
  /**
   * SN-8310 — add one more charge to an INVOICED player's share. Optional so a
   * caller that does not support it simply omits the kebab entry.
   */
  onAddInvoice?: (slotId: string) => void
}

/**
 * The kebab affordance matrix (D20 + D21). Unlock keys off locked_at (so a locked
 * $0/paid slot still offers it); Lock on a pending slot — ALLOWED under header lock
 * (D20/D36: header lock never blocks lockSlot, and the backend permits it); Release
 * for a cancelled slot or an unlocked-occupied one (header-unlocked); Delete only for
 * an open slot (header-unlocked). The server 422 stays authoritative for blockers the
 * client can't see (trashed-paid / in-flight).
 */
/**
 * SN-8357 — the roster ids the sheet knows about, or `undefined` when membership is
 * not known yet (the roster query has not resolved, or the caller has no access to it).
 *
 * `undefined` deliberately means UNKNOWN rather than EMPTY: an empty set would hide
 * Edit Player on every row while the roster is still loading, and a caller that never
 * fetches the roster would lose the action entirely. Where membership IS known, it is
 * the only test — an INVOICED occupant who is on the roster keeps the action, and a
 * pending one who is not on it loses it.
 */
export type RosterPlayerIds = ReadonlySet<string> | undefined | null

/**
 * Whether THIS row's occupant can actually be edited on the roster.
 *
 * `onEditPlayer` is bound once for the whole grid, so it can only ever answer "may this
 * caller edit the roster at all" — never "is this particular occupant on it". That gap
 * is what offered the action on every occupied row and produced a dead-end error on
 * click. `slot.player.id` is the exact id the click handler checks against the roster
 * map, so checking it here makes the menu and the click agree.
 */
const canEditOccupant = (
  row: BalanceTableRow,
  rosterPlayerIds: RosterPlayerIds
): boolean => {
  // Only a PLAYER is on the roster — an official line has no player to edit.
  if (!isFilledPlayerSlot(row.slot)) {
    return false
  }
  if (rosterPlayerIds === undefined || rosterPlayerIds === null) {
    return true
  }
  const playerId = row.slot.player?.id
  return playerId !== undefined && playerId !== null
    ? rosterPlayerIds.has(String(playerId))
    : false
}

export const kebabItems = (
  row: BalanceTableRow,
  handlers: KebabHandlers,
  rosterPlayerIds?: RosterPlayerIds
): KebabItem[] => {
  const { slot, registration } = row
  const isLocked = slot.locked_at !== null
  // Sep 23 — occupied is no longer "a player holds it": an official's line is occupied
  // by its billing registration. The billing actions follow `occupied`; the roster and
  // slot-management actions stay with player lines only (balanceRowKind).
  const occupied = isOccupiedLine(slot)
  const playerLine = isPlayerLine(slot)
  const isCancelled = slot.status === 'cancelled'
  // D11 — the payer of an official line may never cancel it unless a league admin.
  const ownLineReason = isViewerRestricted(slot)
    ? balanceText('common:balanceOfficialOwnLineTip')
    : undefined

  const items: KebabItem[] = []

  // C-5 — the payments Drawer used to be its own column; it now leads the kebab so
  // there is a single action column. Same gate as the old cell (hasPaymentsDetail):
  // an occupied slot always opens (the rep's note lives there pre-lock); an open
  // slot with no bills/payments has nothing to show.
  if (hasPaymentsDetail(row)) {
    items.push({
      key: 'view-payments',
      label: balanceLabel('common:balanceViewPayments'),
      onClick: () => handlers.onViewPayments(slot.id),
    })
  }

  // SN-4709 follow-up — open the full roster Edit Player editor for the occupant.
  // Gated on the OPTIONAL onEditPlayer handler: the caller supplies it only when
  // the roster row is loadable and editable, so it is absent for an open slot and
  // for a caller without roster-edit access.
  if (handlers.onEditPlayer && canEditOccupant(row, rosterPlayerIds)) {
    items.push({
      key: 'edit-player',
      label: balanceLabel('common:balanceEditPlayer'),
      onClick: () => handlers.onEditPlayer?.(slot.id),
    })
  }

  // Sep 23 (F3) — the same rule as the row's Send Invoice button: a former official's
  // line and one kept at $0 for its history are never offered an invoice.
  if (isInvoiceableLine(slot)) {
    items.push({
      key: 'lock',
      label: balanceLabel('common:lockSlot'),
      onClick: () => handlers.onLockSlot(slot.id),
    })
  }
  if (isLocked) {
    items.push({
      key: 'unlock',
      label: balanceLabel('common:unlockSlot'),
      disabled: ownLineReason !== undefined,
      disabledReason: ownLineReason,
      onClick: () => handlers.onUnlockSlot(slot.id),
    })
  }
  // SN-8310 (Sep 22) — Add Invoice: any invoiced, occupied, live slot, whatever it
  // owes (a paid-up player can still be charged more). The server 422s the rest.
  // Sep 23 (D5) — never on a former official's line: it takes no new money.
  if (
    handlers.onAddInvoice &&
    isLocked &&
    occupied &&
    !isCancelled &&
    takesNewMoney(slot)
  ) {
    items.push({
      key: 'add-invoice',
      label: balanceLabel('common:balanceAddInvoice'),
      onClick: () => handlers.onAddInvoice?.(slot.id),
    })
  }
  // An official's line is never released — it belongs to its person, not to a spot
  // a player can take.
  if (playerLine && (isCancelled || (occupied && !isLocked))) {
    items.push({
      key: 'release',
      label: balanceLabel('common:releaseSlot'),
      onClick: () => handlers.onReleaseSlot(slot.id),
    })
  }
  if (isOpenPlayerSlot(slot)) {
    items.push({
      key: 'delete',
      danger: true,
      label: balanceLabel('common:deleteSlot'),
      onClick: () => handlers.onDeleteSlot(slot.id),
    })
  }
  /*
   * SN-4709 × SN-8081 (D46) — the per-slot Player Finder opt-in, open slots only.
   *
   * REMOVE is offered whenever the slot is listed, and is deliberately NOT gated
   * on `can_list_in_player_finder`: turning the competition's finder off (or the
   * registration ceasing to qualify) does not clear the flag, so gating removal
   * would leave a rep looking at a live ad with no way to withdraw it.
   *
   * LIST is gated on that same server-computed flag (root finder on + approved
   * registration + no other recruiting registration — C7/C8), so the rep is never
   * offered an action the server will refuse. Listing an unlisted slot also turns
   * the team's recruiting back on if it was switched off.
   *
   * EDIT POSITION is for a slot that is already listed, advertised or hidden. It
   * is not an opt-in, so the gate does not apply, and it leaves recruiting as it
   * is.
   */
  if (isOpenPlayerSlot(slot)) {
    if (isListedInFinder(slot)) {
      if (handlers.onEditFinderPosition) {
        items.push({
          key: 'finder-edit-position',
          label: balanceLabel('common:balanceEditFinderPosition'),
          onClick: () => handlers.onEditFinderPosition?.(slot.id),
        })
      }
      if (handlers.onRemoveFromFinder) {
        items.push({
          key: 'finder-remove',
          label: balanceLabel('common:balanceRemoveFromFinder'),
          onClick: () => handlers.onRemoveFromFinder?.(slot.id),
        })
      }
    } else if (
      handlers.onListInFinder &&
      registration.can_list_in_player_finder
    ) {
      items.push({
        key: 'finder-list',
        label: balanceLabel('common:balanceListInFinder'),
        onClick: () => handlers.onListInFinder?.(slot.id),
      })
    }
  }
  // SN-4709 Phase 8: an offline payment targets the slot's player (pre- or post-lock, D-C);
  // never a cancelled or open slot. The server 422s a pre-lock $0-credit edge if needed.
  // Sep 23 — an official's line takes cash like a player's share.
  if (occupied && !isCancelled) {
    items.push({
      key: 'record-offline',
      label: balanceLabel('common:balanceRecordOffline'),
      onClick: () => handlers.onRecordOffline(slot.id),
    })
  }
  // SN-4709 follow-up: a payment reminder only makes sense for a LOCKED, occupied,
  // non-cancelled slot that still OWES (there is a real debt to chase). The server
  // re-checks and 422s a slot that is not remindable. A former official's debt stays
  // remindable (D5).
  if (isRemindableSlot(slot)) {
    items.push({
      key: 'remind',
      label: balanceLabel('common:balanceRemindSlot'),
      onClick: () => handlers.onRemindSlot(slot.id),
    })
  }

  return items
}

/**
 * Whether the Payments cell has anything to open. Shared with the keyboard
 * handler on purpose: without it, Enter on a VISUALLY EMPTY Payments cell would
 * still open the drawer, because the handler would have no idea the renderer
 * drew nothing.
 *
 * A2 — an OCCUPIED slot always opens, even with no bills and no payments. The
 * drawer is now also where the rep's note about that player lives, and bills
 * only exist AFTER lock: gating on bills/payments alone made the note
 * unreachable for exactly the pre-lock case it was asked for ("he told me he'd
 * pay Friday"). An OPEN slot still has nothing to show and stays closed.
 */
export const hasPaymentsDetail = (row: BalanceTableRow): boolean =>
  isOccupiedLine(row.slot) ||
  row.slot.billing_schedules.length > 0 ||
  (row.slot.offline_payments?.length ?? 0) > 0

/**
 * SN-4709 (James, Jul 31) — the ONE rule for what a row's visible ACTION is.
 *
 *   invoice  the slot holds a player and nothing has been requested yet
 *   remind   the invoice went out and a balance is still due
 *   null     nothing to do (open, cancelled, or paid up)
 *
 * Shared by the Action cell, the grid's `isRowSelectable` and the toolbar's
 * "Send N Invoices", so the button on a row, the checkbox beside it and the
 * count in the toolbar can never disagree about which rows are actionable.
 * The server re-checks and 422s either way.
 */
export const rowPrimaryAction = (
  row: BalanceTableRow
): 'invoice' | 'remind' | null => {
  if (isInvoiceable(row)) {
    return 'invoice'
  }
  return isRemindable(row) ? 'remind' : null
}

/**
 * A slot ready to be invoiced. `pending` already means "someone holds the line
 * and nothing has been invoiced" (TeamRegistrationSlot::deriveStatus). Sep 23 (F3)
 * — an official's line also has to be current and above $0 (`isInvoiceableLine`).
 */
export const isInvoiceable = (row: BalanceTableRow): boolean =>
  isInvoiceableLine(row.slot)

/**
 * A slot worth chasing: invoiced, still occupied, not cancelled, still owing.
 * The SAME predicate BalanceTab counts for the bulk "Remind Unpaid" hint and
 * that `remindableSlots()` re-applies server-side.
 */
export const isRemindable = (row: BalanceTableRow): boolean =>
  isRemindableSlot(row.slot)

/**
 * Whether the ⋮ menu has any action. Same reason as above — a disabled kebab
 * must not be keyboard-openable either.
 */
export const hasKebabItems = (
  row: BalanceTableRow,
  handlers: KebabHandlers,
  rosterPlayerIds?: RosterPlayerIds
): boolean => kebabItems(row, handlers, rosterPlayerIds).length > 0

/**
 * D20 editability for PLAYER SHARE.
 *
 * James, Jul 31 — the two SHEET-LEVEL gates are gone with the toggles that set
 * them:
 *
 *   `auto`   Auto-Split retired in favour of an advisory Even Split column plus
 *            an explicit "Split Evenly". The flag survives server-side (it still
 *            splits on creation and redistributes when the admin changes the
 *            fee) and the first manual edit clears it — but with no toggle to
 *            turn it off, keeping it as a precondition would have left every
 *            share permanently uneditable.
 *   `locked` Lock Sheet retired, and a one-shot migration released every row
 *            that was already locked, so nothing can be header-locked any more.
 *
 * Every `guardHeaderUnlocked()` check remains on the server, which stays
 * authoritative for both.
 *
 * The per-SLOT lock stands: an invoiced slot's amount is frozen because billing
 * rows have already been generated against it.
 */
export const isShareEditable = (row: BalanceGridRow | undefined): boolean => {
  if (isCandidateRow(row)) {
    // Sep 23 — typing an amount on a $0 candidate opens their line; not while it is.
    return !row.pending
  }
  if (!isDataRow(row) || row.slot.locked_at !== null) {
    return false
  }

  // A cancelled official line (the team's registration was cancelled) is history.
  return !(isOfficialLine(row.slot) && row.slot.status === 'cancelled')
}
